/**
 * Pilot inputs for one tick. Axes are normalized deflections, not rates — the
 * per-aircraft control-response field turns them into body rates (controls.ts).
 */

import { clamp } from "./units.ts";

export interface FlightInputs {
  /** Elevator: −1 full forward stick, +1 full back stick. */
  readonly pitch: number;
  /** Aileron: −1 full left, +1 full right. */
  readonly roll: number;
  /** Rudder: −1 full left, +1 full right. */
  readonly yaw: number;
  readonly throttle: number;
  readonly firing: boolean;
  /** The U key. Held state, not an edge — guns.ts counts one clear attempt per press. */
  readonly clearJam: boolean;
  /**
   * The bomb toggle. Held state, like `firing`: the rack's own
   * `bombReleaseIntervalSeconds` spaces the stick (`combat/bomb-pass.ts`), so
   * holding it walks bombs off at the same cadence a bomber's does rather than
   * dumping the load on one tick.
   *
   * False for every aeroplane that is not carrying anything, which is all of
   * them unless a mission handed the player a load — the original never did
   * (docs/game/player-bombing.md), so this is a declared port extension.
   */
  readonly bombRelease: boolean;
}

export const NEUTRAL_INPUTS: FlightInputs = {
  pitch: 0,
  roll: 0,
  yaw: 0,
  throttle: 0,
  firing: false,
  clearJam: false,
  bombRelease: false,
};

export function makeInputs(overrides: Partial<FlightInputs> = {}): FlightInputs {
  return { ...NEUTRAL_INPUTS, ...overrides };
}

/**
 * Axis resolution for anything that has to *write a stick down* — a duel frame
 * on the wire (`net/protocol.ts`) or a replay tape (`replay/`). One
 * thousandth of full deflection is far below what a hand or a keyboard ramp can
 * distinguish and costs at most five characters to spell.
 *
 * The *arithmetic* is part of the contract, not only the resolution:
 * `round(v * SCALE) / SCALE` and `round(v / QUANTUM) * QUANTUM` are not the
 * same function in binary floating point, so both the wire codec and the tape
 * spell it the first way. `net/test/protocol.test.ts` pins the two against each
 * other.
 */
export const AXIS_SCALE = 1000;

/**
 * The stick as a recorder or a transmitter will store it.
 *
 * The rule this exists to enforce is AD-008's, and it is the one that decides
 * whether a recording is a recording at all: **quantise on the way *in*, not
 * only on the way out.** If the sim steps on a raw 0.7333333333333333 and the
 * tape keeps 0.733, the replay flies a different aeroplane from the first tick
 * and the divergence has no visible cause. So the live flight simulates with
 * this value too, and the tape stores exactly what was flown.
 *
 * Idempotent, which is what makes it safe to apply at both ends.
 */
export function quantizeInputs(inputs: FlightInputs): FlightInputs {
  return {
    pitch: quantizeAxis(inputs.pitch, -1),
    roll: quantizeAxis(inputs.roll, -1),
    yaw: quantizeAxis(inputs.yaw, -1),
    throttle: quantizeAxis(inputs.throttle, 0),
    firing: inputs.firing,
    clearJam: inputs.clearJam,
    bombRelease: inputs.bombRelease,
  };
}

/** The `+ 0` collapses negative zero, which JSON cannot round-trip — see `replay/input-trace.ts`. */
function quantizeAxis(value: number, minimum: number): number {
  return (Math.round(clamp(value, minimum, 1) * AXIS_SCALE) + 0) / AXIS_SCALE;
}
